import React, { useEffect, useState } from "react";
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  TextField,
  Button,
  Box,
  IconButton,
  Divider,
  TextareaAutosize,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import { setAlert } from "../../../redux/modules/alert";
import { getClientName } from "../../../redux/modules/auth";
import { useSelector } from "react-redux";

const EmailTypeSelector = ({
  emailType,
  setEmailType,
  plainData,
  setPlainData,
}) => {
  const dispatch = useDispatch();

  const clientName = useSelector((state) => state?.auth?.clientData);

  const [isSenderEdited, setIsSenderEdited] = useState(false);

  const handleTypeChange = (e) => {
    setEmailType(e.target.value);
    if (e.target.value === "plain") {
      dispatch(getClientName());
    }
  };

  const handlePlainChange = (field, value) => {
    setPlainData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;

      if (file.size > maxSize) {
        dispatch(
          setAlert(
            `File size exceeds 5MB. Please upload a smaller image.`,
            "info"
          )
        );
        e.target.value = "";
        return;
      }
      const previewURL = URL.createObjectURL(file);
      setPlainData((prev) => ({
        ...prev,
        [field]: file,
        [`${field}Preview`]: previewURL,
        [`${field}Removed`]: false,
      }));
    }
  };

  const removeFile = (field) => {
    setPlainData((prev) => ({
      ...prev,
      [field]: null,
      [`${field}Preview`]: null,
      [`${field}Removed`]: true,
    }));
  };

  return (
    <div>
      <Paper
        elevation={0}
        sx={{
          mt: 2,
        }}
      >
        <Typography variant="h6" style={{ fontSize: 16 }} gutterBottom>
          Email Type
        </Typography>

        <RadioGroup
          row
          value={emailType}
          onChange={handleTypeChange}
          sx={{ mb: 1 }}
        >
          <FormControlLabel
            value="branded"
            control={<Radio color="primary" />}
            label="Branded (Default Template)"
          />
          <FormControlLabel
            value="plain"
            control={<Radio color="primary" />}
            label="Plain Email"
          />
        </RadioGroup>

        {emailType === "plain" && (
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              background: "#fff",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Plain Email Settings
            </Typography>

            <TextField
              size="small"
              fullWidth
              margin="dense"
              label="Sender Name"
              value={
                isSenderEdited
                  ? plainData?.senderName
                  : plainData?.senderName || clientName?.client_name || ""
              }
              onChange={(e) => {
                setIsSenderEdited(true);
                handlePlainChange("senderName", e.target.value);
              }}
            />

            <Divider sx={{ padding: "8px" }} />

            <Typography
              variant="subtitle2"
              mt={1}
              fontWeight={600}
              gutterBottom
            >
              Email Signature Details
            </Typography>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Personal Information{" "}
              {!plainData?.fullName && (
                <>
                  <span style={{ color: "red", fontWeight: 500 }}>
                    <br /> Full Name is required
                  </span>
                  , other fields are optional.
                </>
              )}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                mt: 1.5,
                mb: 0.5,
              }}
            >
              <TextField
                label="Full Name*"
                size="small"
                value={plainData?.fullName || ""}
                onChange={(e) => handlePlainChange("fullName", e.target.value)}
                sx={{ flex: 1 }}
              />

              <TextField
                label="Designation / Role"
                size="small"
                value={plainData?.designation || ""}
                onChange={(e) =>
                  handlePlainChange("designation", e.target.value)
                }
                sx={{ flex: 1 }}
              />
            </Box>

            <Typography
              variant="caption"
              color="textSecondary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              Contact Information
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                mt: 1,
                mb: 0.5,
              }}
            >
              <TextField
                label="Email"
                size="small"
                value={plainData?.email || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[\w-.@]*$/.test(value)) {
                    handlePlainChange("email", value);
                  }
                }}
                sx={{ flex: 1 }}
                inputProps={{
                  inputMode: "email",
                }}
              />
              <TextField
                label="Phone Number"
                size="small"
                value={plainData?.phone || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[0-9+\-\s]*$/.test(value)) {
                    handlePlainChange("phone", value);
                  }
                }}
                sx={{ flex: 1 }}
              />
            </Box>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Address
            </Typography>

            <Box>
              <TextField
                size="small"
                fullWidth
                margin="dense"
                label="Address"
                multiline
                rows={3}
                value={plainData?.address || ""}
                onChange={(e) => handlePlainChange("address", e.target.value)}
              />
            </Box>

            <Divider sx={{ padding: "8px" }} />

            {/* Logo Upload */}
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" display="block" gutterBottom>
                Company Logo
              </Typography>
              {!plainData?.logoPreview ? (
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{ textTransform: "none" }}
                >
                  Upload Logo
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleFileChange("logo", e)}
                  />
                </Button>
              ) : (
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <img
                    src={plainData?.logoPreview}
                    alt="Logo Preview"
                    style={{
                      maxWidth: 60,
                      height: "auto",
                      borderRadius: 8,
                      marginTop: 8,
                      border: "1px solid #ccc",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeFile("logo")}
                    sx={{
                      position: "absolute",
                      top: "12px",
                      right: "4px",
                      background: "rgba(255,255,255,0.8)",
                      padding: "2px",
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </Paper>
    </div>
  );
};

export default EmailTypeSelector;
